/** @prettier */
import React, { useState } from "react";
import ReactTable, { ControlledStateOverrideProps } from "react-table";
import moment from "moment";
import Pagination from "../../CommonComponents/Pagination";
import "./TestList.css";
import { Button, ButtonGroup, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { LinkButton } from "../../util/LinkButton";
import { TestListData, getTests, renameTest } from "./testListApi";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { displayTestMode } from "../models";
import { Subscription } from "../../services/qbankApi";

const PAGE_SIZE = 25;

const headerStyle = { fontSize: "17px" };
const getTableStyle = () => {
    return { style: { height: "100%" } };
};

function resetTableScroll() {
    // Used to fix the table scroll on pagination change
    // https://github.com/tannerlinsley/react-table/issues/772
    const tableEl = document.getElementsByClassName("rt-tbody")[0];
    if (tableEl) {
        tableEl.scrollTop = 0;
    }
}

const testScore = (correct: number, total: number) => {
    const percent = Math.round((100 * correct) / total);
    const hue = (correct / total) * 120;
    const color = `hsl(${hue}, 100%, 40%)`;
    return (
        <span style={{ color }} className={classNames("test-score")}>
            {percent}%
        </span>
    );
};

type ActionsCellProps = {
    test: TestListData;
    renameTest: (id: string) => void;
};
const ActionsCell = ({ test, renameTest }: ActionsCellProps) => (
    <ButtonGroup bsSize="xsmall">
        {test.status !== "FINISHED" ? (
            <LinkButton title="Take Test" to={`tests/${test.uuid}/run`}>
                <FontAwesomeIcon icon="play" />
            </LinkButton>
        ) : (
            <>
                <LinkButton
                    title="Test Results"
                    to={`tests/${test.uuid}/results`}
                >
                    <FontAwesomeIcon icon="chart-bar" />
                </LinkButton>
                <LinkButton
                    title="Test Review"
                    to={`tests/${test.uuid}/review`}
                >
                    <FontAwesomeIcon icon="eye" />
                </LinkButton>
            </>
        )}
        <Button
            bsStyle="link"
            title="Rename test"
            onClick={() => {
                renameTest(test.uuid);
            }}
        >
            <FontAwesomeIcon icon="pencil-alt" />
        </Button>
    </ButtonGroup>
);

type RenameTestModalProps = {
    testName: string;
    setName: (newName: string) => Promise<void>;
    closeModal: () => void;
};
const RenameTestModal = ({
    testName,
    setName,
    closeModal,
}: RenameTestModalProps) => {
    const [newTestName, setNewTestName] = useState(testName);
    return (
        <Modal show={true} onHide={closeModal}>
            <Modal.Header>
                <Modal.Title>Rename Test</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                Test Name:
                <input
                    type="text"
                    value={newTestName}
                    onChange={(e) => setNewTestName(e.currentTarget.value)}
                    autoFocus={true}
                ></input>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle="link" onClick={closeModal}>
                    Cancel
                </Button>
                <Button
                    bsStyle="primary"
                    disabled={testName === newTestName}
                    onClick={() => setName(newTestName).then(closeModal)}
                >
                    Rename
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

type TestListModal = { kind: "renamingTest"; id: string };
type TestListProps = {
    userId: string;
    activeSubscription: Subscription;
};
const TestList = ({ userId, activeSubscription }: TestListProps) => {
    const [loading, setLoading] = useState(true);
    const [tests, setTests] = useState<TestListData[]>([]);
    const [pageCount, setPageCount] = useState(0);
    const [modalState, showModal] = useState<TestListModal>();

    const productId = activeSubscription.product.uuid;
    return (
        <div className="test-list">
            <div className="dashboard-header">
                <h3 className="dashboard-title">My Tests</h3>
                <Link to="/tests/create">
                    <Button bsStyle="primary">Create Test</Button>
                </Link>
            </div>
            <div className="grid-container">
                <ReactTable<TestListData>
                    loading={loading}
                    data={tests}
                    className="-striped -highlight"
                    getProps={getTableStyle}
                    manual // Server-data
                    pages={pageCount}
                    pageSize={PAGE_SIZE}
                    defaultPageSize={PAGE_SIZE}
                    PaginationComponent={Pagination}
                    sortable={false}
                    noDataText={!loading && "No Tests Created"}
                    onFetchData={(state: ControlledStateOverrideProps) => {
                        const sortParams = state.sorted[0]
                            ? ({
                                  sortField: state.sorted[0].id,
                                  sortOrder: state.sorted[0].desc
                                      ? "DESC"
                                      : "ASC",
                              } as const)
                            : {};
                        setLoading(true);
                        getTests(userId, productId, {
                            size: state.pageSize!,
                            page: state.page!,
                            ...sortParams,
                        }).then(({ content, totalPages }) => {
                            setPageCount(totalPages);
                            setTests(content);
                            setLoading(false);
                            resetTableScroll();
                        });
                    }}
                    columns={[
                        {
                            Header: "Created",
                            id: "created",
                            width: 100,
                            headerStyle,
                            accessor: ({ createdDate }) =>
                                moment(createdDate).format("YYYY-MM-DD"),
                            sortable: true,
                        },
                        {
                            Header: "Status",
                            accessor: "status",
                            minWidth: 30,
                            width: 60,
                            headerStyle,
                            className: "cell-center",
                            Cell: (row) => (
                                <FontAwesomeIcon
                                    icon={
                                        row.value !== "FINISHED"
                                            ? ["far", "clock"]
                                            : "check-circle"
                                    }
                                />
                            ),
                        },
                        {
                            Header: "Test Name",
                            accessor: "name",
                            headerStyle,
                            sortable: true,
                        },
                        {
                            id: "mode",
                            Header: "Mode",
                            width: 100,
                            headerStyle,
                            accessor: ({ mode }) => displayTestMode(mode),
                            sortable: true,
                        },
                        {
                            id: "answered",
                            Header: "Answered",
                            width: 100,
                            headerStyle,
                            accessor: ({
                                numAnsweredQuestions,
                                numQuestions,
                            }) => `${numAnsweredQuestions}/${numQuestions}`,
                            sortable: true,
                        },
                        {
                            id: "correct",
                            Header: "Correct",
                            width: 100,
                            headerStyle,
                            accessor: ({
                                status,
                                numCorrectQuestions,
                                numQuestions,
                            }) =>
                                status === "FINISHED"
                                    ? `${numCorrectQuestions}/${numQuestions}`
                                    : "",
                            sortable: true,
                        },
                        {
                            id: "score",
                            Header: "Score",
                            width: 100,
                            headerStyle,
                            accessor: ({
                                status,
                                numCorrectQuestions,
                                numQuestions,
                            }) =>
                                status === "OPEN"
                                    ? ""
                                    : testScore(
                                          numCorrectQuestions,
                                          numQuestions,
                                      ),
                            sortable: true,
                        },
                        {
                            Header: "Actions",
                            headerStyle,
                            Cell: ({ original: test }) => (
                                <div className="test-list-actions">
                                    <ActionsCell
                                        test={test}
                                        renameTest={(id) =>
                                            showModal({
                                                kind: "renamingTest",
                                                id,
                                            })
                                        }
                                    />
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
            {modalState && (
                <RenameTestModal
                    testName={
                        tests.find(({ uuid }) => uuid === modalState.id)
                            ?.name ?? ""
                    }
                    setName={async (newName) => {
                        await renameTest(modalState.id, newName);
                        // Update the list to show the new name
                        setTests(
                            tests.map(
                                (test): TestListData =>
                                    test.uuid !== modalState.id
                                        ? test
                                        : { ...test, name: newName },
                            ),
                        );
                    }}
                    closeModal={() => showModal(undefined)}
                />
            )}
        </div>
    );
};

export default TestList;
